import { PRICE_FIELDS, type Prices } from '@services/amendments/types'
import { TrendingUp } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { FlatDetail, RentModel } from 'types/api'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

import { DepositAmendmentDialog } from './DepositAmendmentDialog'
import { RentAmendmentDialog } from './RentAmendmentDialog'
import { RentAmendmentHistory } from './RentAmendmentHistory'

/** Rent changes agreed with the landlord, on the flat's contracts tab. */
export function FlatRentAmendments({ flat }: { flat: FlatDetail }) {
  const { t } = useTranslation('amendments')
  const [open, setOpen] = useState(false)
  const [depositOpen, setDepositOpen] = useState(false)

  const current = Object.fromEntries(
    PRICE_FIELDS.map((f) => [f, Number(flat[f] ?? 0)])
  ) as Prices
  // The latest generated deposit payout to the landlord (due_date order).
  const depositPayment = ((flat.flat_payments ?? []) as unknown as RentModel[])
    .filter((p) => p.payment_type === 'LANDLORD_DEPOSIT')
    .at(-1)

  return (
    <Card className="space-y-4 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <TrendingUp className="h-5 w-5" />
          </span>
          <h3 className="m-0 font-display text-base font-bold tracking-tight text-ink">
            {t('landlord_card_title')}
          </h3>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDepositOpen(true)}
          >
            {t('change_deposit')}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
            {t('increase_rent')}
          </Button>
        </div>
      </div>
      <p className="m-0 text-sm text-muted-foreground">
        {t('landlord_card_hint')}
      </p>
      <RentAmendmentHistory flatId={flat.id} showTitle={false} />
      <RentAmendmentDialog
        side="LANDLORD"
        flatId={flat.id}
        current={current}
        open={open}
        onOpenChange={setOpen}
      />
      {flat.id && (
        <DepositAmendmentDialog
          flatId={flat.id}
          current={Number(flat.deposit ?? 0)}
          depositPayment={depositPayment}
          prices={current}
          open={depositOpen}
          onOpenChange={setDepositOpen}
        />
      )}
    </Card>
  )
}
